import { endPoints } from "./urls"
import { IAdminAddUserPayload, IAdminUpdateUserPayload, IContactUsPayload, IForgotPassword, ILoginPayload, ILoginResponse, IRegisterPayload, IRegisterResponse, IResetPassword, ITransactionResponse, IUserData, IVerifyPayload, NotificationResponse } from "types/userTypes"
import { IClient, ICompany, ICompanyPackage, IOrder } from "types/tablesTypes";
import { IAddClientData, IAddCompanyData, IClientResponse, MeshulamPayload, NegotiationData, NewDealPayload, PaymentPayload } from "./types";
import apiClient from "./apiClient";
import { FeeSteps } from "types/formTypes";
import axios from "axios";
import { env } from "env";
import { store } from "store";

async function axiosPost<T>(url: string, data: any, customHeaders: any = {}) {
    try {
        const headers = {
            ...apiClient.defaults.headers,
            ...customHeaders,
        };

        const result = await apiClient.post<T>(url, data, { headers });
        return result.data;
    } catch (exception: any) {
        if (exception.response?.data) {
            throw exception.response.data;
        }
        throw exception;
    }
}


async function axiosGet<T>(url: string) {
    try {
        const result = await apiClient.get<T>(url);
        return result.data;
    } catch (exception: any) {
        if (exception.response?.data) {
            throw exception.response.data;
        }
        throw exception;
    }
}

async function axiosPut<T>(url: string, data: any) {
    try {
        const result = await apiClient.put<T>(url, data);
        return result.data;
    } catch (exception: any) {
        if (exception.response?.data) {
            throw exception.response.data;
        }
        throw exception;
    }
}

async function axiosDelete<T>(url: string, customHeaders: any = {}) {
    try {
        const headers = {
            ...apiClient.defaults.headers,
            ...customHeaders,
        };

        const result = await apiClient.delete<T>(url, { headers });
        return result.data;
    } catch (exception: any) {
        if (exception.response?.data) {
            throw exception.response.data;
        }
        throw exception;
    }
}



export function apiLogin(payload: ILoginPayload) {
    return axiosPost<ILoginResponse>(`${endPoints.LOGIN}`, payload);
}

export function apiVerify(payload: IVerifyPayload) {
    return axiosPost<ILoginResponse>(`${endPoints.VERIFY}`, payload);
}

export function apiSendIgovRequests(payload: any) {
    return axiosPost<any>(`${endPoints.IGOV}`, payload);
}

export function apiGetUserDetails() {
    return axiosGet<IUserData>(`${endPoints.USER}/me?populate[createdByAdmin]=*&populate[userImage]=*&populate[company][populate][company_admin]=*`);
}

export function apiGetTransactions(page: number, filter?: string | null, exclude?: string | null, favorite?: boolean) {
    let url = `${endPoints.TRANSACTION}?pagination[page]=${page}&sort=createdAt:desc&populate=*`;
    if (filter) {
        url += `&filters[transactionStep][$eq]=${filter}`;
    }

    if (exclude) {
        url += `&filters[transactionStep][$ne]=${exclude}`;
    }
    if (favorite) {
        url += `&filters[favorite][$eq]=true`;
    }

    return axiosGet<ITransactionResponse>(url);
}

export function calculateFeeSum(company: number, year: number) {
    return axiosGet<any>(`${endPoints.TRANSACTION}/calculate-fee-sum?company_id=${company}&year=${year}`)
}

export function getGraphDetails(company: number, year: number) {
    return axiosGet<any>(`${endPoints.TRANSACTION}/graph-details?company_id=${company}&year=${year}`)
}

export function getPieChartDetails(company: number, year: number) {
    return axiosGet<any>(`${endPoints.TRANSACTION}/pie-chart-details?company_id=${company}&year=${year}`)
}


export function apiUpdateTransaction(id: number | string, payload: any) {
    return axiosPut<ITransactionResponse>(`${endPoints.TRANSACTION}/${id}`, { data: payload })
}

export function apiSearchTransaction(searchQuery: string) {
    return axiosGet<any>(`${endPoints.TRANSACTION}?_q=${searchQuery}`)
}

export function apiRegister(payload: IRegisterPayload) {
    return axiosPost<IRegisterResponse>(`${endPoints.REGISTER}`, payload)
}

export function apiAdminAddUser(payload: IAdminAddUserPayload) {
    return axiosPost<any>(`${endPoints.ADMIN_USERS}/register`, payload)
}

export function apiAdminUpdateUser(payload: IAdminUpdateUserPayload, id: number) {
    return axiosPut<any>(`${endPoints.ADMIN_USERS}/update/${id}`, payload)
}

export function apiDeleteAdminUser(id: number) {
    return axiosDelete<any>(`${endPoints.ADMIN_USERS}/delete/${id}`)
}


export function apiForgotPassword(payload: IForgotPassword) {
    return axiosPost<any>(`${endPoints.FORGOT_PASSWORD}`, payload)
}

export function apiResetPassword(payload: IResetPassword) {
    return axiosPost<any>(`${endPoints.RESET_PASSWORD}`, payload)
}

export function apiSendReport(payload: IContactUsPayload) {
    return axiosPost<any>(`${endPoints.CONTACT_US}`, payload)
}

export function apiGetAllClients() {
    return axiosGet<IClientResponse>(`${endPoints.CLIENT}?pagination[limit]=1000`).then(response => {
        return response.data.map(client => {
            const { id, ...attributes } = client.attributes;
            return { id: client.id, ...attributes };
        });
    });
}

export function apiGetPropertyTypes() {
    return axiosGet<any>(`${endPoints.PROPERTY_TYPE}`);
}


export function apiGetCheckList() {
    return axiosGet<any>(`${endPoints.CHECK_LIST}`);
}

export function apiGetClients(page: number, filter?: string | null, exclude?: string | null) {

    let url = `${endPoints.CLIENT}?pagination[page]=${page}&sort=createdAt:desc&populate[transactions][populate]=payment_steps`;
    if (filter) {
        url += `&filters[transactions][transactionStep][$eq]=${filter}`;
    }

    if (exclude) {
        url += `&filters[transactions][transactionStep][$ne]=${exclude}`;
    }


    return axiosGet<IClientResponse>(url);

}

export function apiAddClient(payload: IClient) {
    return axiosPost<IAddClientData>(`${endPoints.CLIENT}`, { data: payload });
}

export function apiUpdateClient(id: number, payload: any) {
    return axiosPut<IAddClientData>(`${endPoints.CLIENT}/${id}`, { data: payload })
}

export function apiDeleteClient(id: number) {
    return axiosDelete<any>(`${endPoints.CLIENT}/${id}`)
}


export function apiDeleteAllMyUsers(id: string) {
    return axiosDelete<any>(`${endPoints.ADMIN_USERS}/deleteAll/${id}`)
}


export function apiAddOrder(payload: IOrder) {
    return axiosPost<any>(`${endPoints.ORDER}`, { data: payload });
}

export function apiUpdateOrder(id: number, payload: any) {
    return axiosPut<any>(`${endPoints.ORDER}/${id}`, { data: payload })
}

export function apiGetTransactionOrders(transactionId: string) {
    return axiosGet<any>(`${endPoints.ORDER}?filters[transaction][id][$eq]=${transactionId}&populate[0]=other_side_client&populate[1]=zip_files`)
}



export async function apiCreatePayment(payload: MeshulamPayload): Promise<any> {
    try {
        const formData = new FormData();
        const token = store.getState().user.jwt;

        Object.entries(payload).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                formData.append(key, value);
            }
        });

        // const url = `http://localhost:1337/api${endPoints.PAYMENT}`
        const response = await axios.post(
            `${env.API_URL}${endPoints.PAYMENT}`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`,
                },
            }
        );

        return response.data;
    } catch (error) {
        console.error('Error creating payment:', error);
        throw error;
    }
}

export function apiAddOtherStep(payload: any) {
    return axiosPost<any>(`${endPoints.OTHER_STEP}`, { data: payload });
}


export function apiUpdateOtherStep(id: number | string, payload: any) {
    return axiosPut<unknown>(`${endPoints.OTHER_STEP}/${id}`, { data: payload })
}


export function apiAddStepPayment(payload: FeeSteps) {
    return axiosPost<any>(`${endPoints.STEPS}`, { data: payload });
}

export function apiUpdateStepPayment(id: number, payload: any) {
    return axiosPut<unknown>(`${endPoints.STEPS}/${id}`, { data: payload })
}

export function apiDeleteStep(id: number) {
    return axiosDelete<any>(`${endPoints.STEPS}/${id}`)
}


export function apiAddOtherPartyClient(payload: IClient) {
    return axiosPost<IAddClientData>(`${endPoints.OTHER_CLIENT}`, { data: payload });
}

export function apiUpdateOtherClient(id: number, payload: any) {
    return axiosPut<IAddClientData>(`${endPoints.OTHER_CLIENT}/${id}`, { data: payload })
}

export function apiAddCompany(payload: ICompany) {
    return axiosPost<IAddCompanyData>(`${endPoints.COMPANY}`, { data: payload })
}

export function apiUpdateCompany(id: number, payload: ICompanyPackage) {
    return axiosPut<IAddCompanyData>(`${endPoints.COMPANY}/${id}`, { data: payload })
}


export function apiCreatePaymentEntry(payload: any) {
    return axiosPost<any>(`${endPoints.PAYMENTS_DATA}`, { data: payload })
}


export function apiDeleteCompany(id: number) {
    return axiosDelete<any>(`${endPoints.COMPANY}/${id}`)
}

export function apiDeleteUser(id: string) {
    return axiosDelete<any>(`${endPoints.USER}/${id}`)
}

export function apiEditUser(payload: IUserData, userId: string) {
    return axiosPut<IUserData>(`${endPoints.USER}/${userId}`, payload);
}

export function apiCreateTransaction(payload: NewDealPayload) {
    return axiosPost<any>(`${endPoints.TRANSACTION}`, { data: payload })
}

export function apiCreateTransactionPayment(payload: PaymentPayload) {
    return axiosPost<any>(`${endPoints.PAYMENTS}`, { data: payload })
}

export function apiDeleteTransactionPayment(id: number) {
    return axiosDelete<any>(`${endPoints.PAYMENTS}/${id}`)
}

export function apiUpdateTransactionPayment(id: number, payload: PaymentPayload) {
    return axiosPut<unknown>(`${endPoints.PAYMENTS}/${id}`, { data: payload })
}


export function apiGetTransactionById(id: string) {
    return axiosGet<any>(`${endPoints.TRANSACTION}/${id}?populate=*`)
}

export function apiGetNegotiations(id: string) {
    return axiosGet<any>(`${endPoints.NEGOTIATION}?filters[transaction][$eq]=${id}`);
}

export function apiGetNotifications() {
    return axiosGet<NotificationResponse>(`${endPoints.NOTIFICATIONS}?pagination[limit]=1000&sort=Notification_time:desc&populate[0]=transaction`);
}

export function apiGetSettings() {
    return axiosGet<any>(`${endPoints.SETTINGS}?populate=*`);
}

export function apiGetMyUsers() {
    return axiosGet<any>(`${endPoints.ADMIN_USERS}/my-users`);
}

export function apiUpdateNotifications(id: number, payload: { Read: boolean }) {
    return axiosPut<unknown>(`${endPoints.NOTIFICATIONS}/${id}`, { data: payload })
}

export function apiCreateNegotiation(payload: NegotiationData) {
    return axiosPost<unknown>(`${endPoints.NEGOTIATION}`, { data: payload })
}

export async function uploadFile(formData: FormData) {
    const headers = {
        'Content-Type': 'multipart/form-data',
    };

    return axiosPost<any>('/upload', formData, headers);
}